import type { LocaleMessageSchema } from '../types';

export default {
  Common: {
    loading: 'Cargando...',
  },
  errors: {
    session_create_error:
      'No se pudo crear la sesión.\nPor favor, inténtalo de nuevo.',
    session_continue_error:
      "No pudimos cargar tu sesión. Por favor, inténtalo de nuevo. Si el problema persiste, contacta con soporte en service{'@'}theartisan.ai.",
    kiosk_not_found:
      'No se pudo cargar el quiosco.\nPor favor, inténtalo de nuevo.',

    art_style_missing: 'No se han añadido estilos de arte a este quiosco',
    art_style_fetch_error: 'No se pudo cargar el estilo de arte',
    art_style_preload_error:
      'No se pudo cargar la vista previa del estilo de arte',

    face_detection_server_error:
      'Error al procesar la foto. Por favor, inténtalo de nuevo.',
    face_detection_preload_error: 'Error al precargar los rostros detectados',
    face_detection_no_faces:
      'No se detectaron rostros. Por favor, intenta tomar otra foto.',

    photo_webcam_not_allowed: 'Permiso de la cámara denegado',
    photo_webcam_not_found:
      'No se encontró la cámara. Comprueba si está conectada al quiosco.',
    photo_webcam_hardware_error:
      'La cámara está ocupada o no funciona.\nInténtalo de nuevo o pide ayuda.',
    photo_webcam_photo_failed: 'No se pudo tomar una foto\ncon la cámara',

    photo_phone_missing_session_id:
      'No pudimos encontrar tu sesión.\nPor favor, escanea el código QR de nuevo.',
    photo_phone_missing_kiosk_slug:
      "Quiosco no encontrado en tu sesión. Si el problema persiste, contacta con soporte en service{'@'}theartisan.ai.",
    photo_phone_limit_exceeded: 'Demasiadas fotos seleccionadas',
    photo_phone_qr_error: 'No se pudo generar el código QR\npara subir fotos',

    photo_file_unsupported_type: 'Tipo de archivo no compatible',
    photo_file_cannot_process:
      'No se puede procesar esta foto.\nPor favor, prueba con otra.',
    photo_file_cannot_process_heic:
      'No se puede procesar la foto HEIC.\nPor favor, prueba con otra.',

    portrait_generation_error:
      'Ocurrió un error al generar tu retrato. Por favor, inténtalo de nuevo.',
    portrait_generation_faces_not_selected:
      'No se han seleccionado rostros para\ngenerar el retrato',
    portrait_generation_style_not_selected:
      'No se ha seleccionado estilo de arte para\ngenerar el retrato',
    portrait_generation_over_limit: 'Demasiados intentos de generación',

    portraits_download_session_error:
      "No pudimos encontrar tu sesión. Comprueba el código introducido. Si el problema persiste, contacta con soporte en service{'@'}theartisan.ai.",
    portraits_download_kiosk_error:
      "Quiosco no encontrado en tu sesión. Si el problema persiste, contacta con soporte en service{'@'}theartisan.ai.",
    portraits_download_portraits_error:
      "No se encontraron retratos en tu sesión. Si el problema persiste, contacta con soporte en service{'@'}theartisan.ai.",

    payment_partner_mode_missing_stripe_account:
      'El modo de pago de socio requiere una cuenta Stripe Connect. Por favor, configura la cuenta Stripe del socio o cambia al modo de pago de plataforma.',

    api_error_missing_session_id: 'Sesión no encontrada',
    api_error_email_code_validation:
      'No se pudo validar el código de pago. Por favor, inténtalo de nuevo.',
  },

  /**
   * Global components
   */

  OverlayError: {
    buttons: {
      close: 'Cerrar',
      restart: 'Empezar de nuevo',
      retry: 'Intentar de nuevo',
    },
  },
  Logo: {
    startOver: 'Empezar de nuevo',
  },
  Copyright: {
    copyright: 'powered by TheArtisan.ai',
    distribution: 'distribuido por',
    terms:
      'Al tomar una foto en este dispositivo, aceptas nuestras políticas y términos. Para revisarlos, por favor',
    termsLink: 'haz clic aquí',
  },
  IdlePopup: {
    title: '¿Aún estás aquí?',
    caption: 'Tu sesión terminará si no estás activo.',
    buttonContinue: 'Sí, estoy aquí',
    buttonRestart: 'No, empezar de nuevo',
  },
  GDPRConsent: {
    title: 'Consentimiento requerido',
    consentText:
      'Necesitamos su consentimiento para procesar sus características faciales (datos biométricos) para crear transformaciones artísticas. Solo puede continuar si tiene 16 años o más (o tiene el consentimiento de los padres).',
    buttonConsent: 'Acepto',
    buttonDecline: 'No acepto',
    privacyPolicyLink: 'Ver política de privacidad',
    declineMessage: 'No puede continuar sin consentimiento',
  },
  StepProgressIndicator: {
    photoShort: 'Foto',
    photoLong: 'Tomar foto',
    facesShort: 'Rostros',
    facesLong: 'Elegir rostros',
    groupPortraitsShort: 'Grupo',
    groupPortraitsLong: 'Retratos de grupo',
    portraitsShort: 'Retratos',
    portraitsLong: 'Elegir retratos',
    printShort: 'Imprimir',
    printLong: 'Pagar e imprimir',
    sendToPickupShort: 'Recogida',
    sendToPickupLong: 'Enviar a recogida',
  },
  UploadedPhotosSidebar: {
    title: 'Tus fotos',
    count: '+{extraPhotoCount} más',
  },
  NumericKeyboard: {
    invalidPin: 'Código PIN inválido',
    clear: 'Borrar',
    enter: 'Entrar',
  },
  OutOfService: {
    message:
      'Tu contrato parece estar en pausa o expirado.\nPor favor, contacta a tu distribuidor para obtener ayuda.',
  },

  /**
   * Steps
   */

  Home: {
    title: '¡Genera tu retrato en segundos!',
    button: '¡Pruébalo ahora!',
  },
  ArtStyle: {
    title: 'Elige tu estilo',
  },
  PhotoFromWebcam: {
    titleGetReady: 'Prepárate para tomar una foto',
    titleAttention: '¡Mira a la cámara!',
    titleReview: '¿Te gusta esta foto?',
    titleUpload: 'Subiendo tu foto…',
    buttonPrimaryPhoto: 'Tomar una foto',
    buttonPrimaryUse: 'Sí, usar esta foto',
    buttonPrimaryRetake: 'Repetir foto',
    buttonSecondaryUpload: 'Subir desde el teléfono',
    buttonSecondaryRetake: 'No, repetir (queda 1 intento)',
    buttonSecondaryUseFirst: 'No, usar la primera foto',
    titleCameraError: 'Cámara no disponible',
    buttonRetryCamera: 'Reintentar cámara',
  },
  PhotoPromptFromPhone: {
    title: '¿Subir fotos desde el teléfono?',
    caption:
      'Sorprende a tus amigos y familiares —\nsube sus fotos para crear retratos.',
    buttonUpload: 'Sí, subir desde el teléfono',
    buttonSkip: 'No, saltar subida desde el teléfono',
  },
  PhotoFromPhone: {
    title: 'Sube fotos desde tu teléfono',
    step1: 'Escanea el código QR con tu teléfono',
    step2: 'Selecciona fotos',
    step3: 'Espera la subida',
    waiting: 'Esperando tu subida…',
    done: 'Subida exitosa',
    buttonCancel: 'Cancelar y volver',
    buttonSkip: 'Saltar subida desde teléfono',
    buttonContinue: 'Continuar',
    gdprConsentAll:
      'Tengo el consentimiento de todas las personas en esta foto para procesar sus características faciales',
    gdprOver16: 'Nadie en esta foto tiene menos de 16 años',
    gdprResponsibility:
      'Acepto la total responsabilidad del cumplimiento del RGPD',
    gdprAgreeToPrefix: 'Acepto los',
    gdprPrivacyPolicyLink: 'términos de política de privacidad',
    gdprAgreeToSuffix: 'para las fotos subidas',
    gdprCheckboxesRequired:
      'Por favor, marque todas las casillas a continuación para continuar',
    buttonSkipKioskPhoto: 'Saltar foto del quiosco',
    buttonTakeWebcamPhoto: 'Tomar foto con webcam',
  },
  FacesIndividual: {
    title: '¿De quién quieres crear retratos individuales?',
    buttonConfirm: 'Confirmar y continuar',
    genderMale: 'Masculino',
    genderFemale: 'Femenino',
    genderNonBinary: 'No binario',
    age0to2: '0-2',
    age3to9: '3-9',
    age10to19: '10-19',
    age20to29: '20-29',
    age30to39: '30-39',
    age40to49: '40-49',
    age50to59: '50-59',
    age60to69: '60-69',
    age70plus: '70+',
    raceWhite: 'Blanco',
    raceBlack: 'Negro',
    raceEastAsian: 'Asiático oriental',
    raceSoutheastAsian: 'Asiático del sudeste',
    raceIndian: 'Indio',
    raceLatino: 'Latino',
    raceMena: 'Medio Oriente',
  },
  Portraits: {
    title: '¡Elige tus retratos!',
    buttonNext: 'Siguiente: Imprimir seleccionados',
    buttonNextGroupOnly: 'Continuar solo con retratos de grupo',
    galleryScrollBack: 'Retratos anteriores',
    galleryScrollForward: 'Retratos siguientes',
    filterLabel: 'Mostrar',
    filterAll: 'Todos',
    filterGroup: 'Juntos',
    filterIndividual: 'Individuales',
    stageKindGroup: 'Retrato de grupo',
    stageKindIndividual: 'Retrato',
    stagePosition: '{index} de {count}',
    stageSelect: 'Seleccionar',
    stageSelected: 'Seleccionado ✓',
    buttonMore: 'Hacer más retratos (quedan {count})',
    popupTitle: '¿Quieres finalizar tu pedido?',
    popupGroupSelected: 'Retratos grupales seleccionados: {count} ({price})',
    popupIndividualSelected:
      'Retratos individuales seleccionados: {count} ({price})',
    popupTotalSum: 'Total: {sum}',
    popupButtonNo: 'No, volver',
    popupButtonYes: 'Sí, imprimir y pagar',
    error: '¡Ups! No se pudo generar este retrato.',
    placeholderGenerating: 'Generando tu retrato…',
    placeholderLoading: 'Cargando tu retrato…',
    groupPlaceholder: 'Tu retrato de grupo aparecerá aquí',
    buttonRetry: 'Intentar de nuevo',
    buttonErrorDetails: 'Detalles del error',
    retrying: 'Reintentando...',
    maxRetriesReached: 'Máx. reintentos alcanzados',
    retryAttempts: 'Intentos: {count}',
    printingLockedPopupTitle:
      'Por favor, llama al empleado más cercano para imprimir tus retratos.',
    generationPendingTitle: 'Algunos retratos todavía se están creando',
    generationPendingCaption:
      'Todavía se están generando {count} retratos. ¿Quieres esperar para verlos todos?',
    generationPendingWait: 'Esperar a todos',
    generationPendingSendReady: 'Enviar {count} retratos listos',
    printingLockedPopupCaption:
      'La impresión en el quiosco no está disponible mientras no haya un empleado presente.',
  },
  PortraitsGroup: {
    buttonMore: 'Hacer más retratos',
  },
  PrintLock: {
    lockTitle: 'Bloquear impresión',
    unlockTitle: 'Desbloquear impresión',
  },
  PaymentType: {
    title: '¿Cómo quieres pagar?',
    optionCardTitle: 'Tarjeta de crédito',
    optionCardCaption: 'Visa, Mastercard, & 10+ tarjetas soportadas',
    optionVoucherTitle: 'Retratos Prepagados',
    optionVoucherCaption: 'Incluidos en la entrada — pida al personal',
  },
  PaymentTerminal: {
    title: 'Por favor, sigue las instrucciones en pantalla',
    titlePaymentSuccess: 'Imprimiendo tus retratos…',
    titlePaymentError: 'Pago fallido',
    titlePrintError1: 'Lo sentimos, algunos retratos no se pudieron imprimir',
    titlePrintError2:
      'Por favor, pulsa "Imprimir de nuevo" abajo para intentar de nuevo.',
    buttonRetryPrinting: 'Imprimir de nuevo',
    buttonRetry: 'Intentar de nuevo',
    buttonCancel: 'Cancelar',
    buttonExitSession: 'Empezar de nuevo',
    buttonBack: 'Volver',
    total: 'Total:',
    captionPayment: 'Esperando tu pago…',
    captionPrinting: 'Impresión exitosa',
    captionPaymentError:
      'Algo salió mal. Por favor, inténtalo de nuevo o empieza de nuevo.',
    captionPaymentFinal: 'El pago no ha funcionado. Pide ayuda al personal.',
    failureCaptions: {
      notEnoughFunds:
        'La tarjeta no tiene fondos suficientes. Prueba con otra tarjeta.',
      expiredCard: 'La tarjeta está caducada. Prueba con otra tarjeta.',
      cardDeclined: 'La tarjeta ha sido rechazada. Prueba con otra tarjeta.',
      cardError: 'No se puede usar esta tarjeta aquí. Prueba con otra tarjeta.',
      processingError:
        'El pago no se ha completado. Inténtalo de nuevo con la misma tarjeta.',
      readerError:
        'El lector de tarjetas no está disponible. Pide ayuda al personal.',
      timeout: 'El pago ha tardado demasiado. Inténtalo de nuevo.',
    },
    errorMessage: 'Lo sentimos, no pudimos procesar tu pago',
    popupCancelledTitle: 'Pago cancelado',
    popupCancelledCaption: '¿Quieres intentarlo de nuevo o empezar de nuevo?',
    popupButtonContinue: 'Intentar de nuevo',
  },
  PaymentVoucher: {
    title:
      'Por favor, contacta a un empleado para obtener ayuda para pagar con un voucher',
    buttonBack: 'Volver',
    pinTitle: 'Pagar con voucher',
  },
  Printing: {
    title: 'Imprimiendo tus retratos…',
    titleError: 'Lo sentimos, algunos retratos no se pudieron imprimir',
    buttonPrintAgain: 'Imprimir de nuevo',
    buttonContinueWithoutPrint: 'Continuar sin imprimir',
    error: '¡Ups! No se pudo imprimir este retrato.',
    errorPartial: '{printed} de {total} impresos',
    retrying: 'Intentando de nuevo…',
    waiting: 'Esperando a la impresora…',
  },
  PrintFailed: {
    title: 'Lo sentimos, no pudimos imprimir tus fotos',
    titlePartial: '{printed} de {total} fotos impresas',
    caption: 'Puedes iniciar una nueva sesión y crear nuevos retratos.',
    captionRefunded: 'Hemos devuelto {amount} a tu tarjeta.',
    captionNotCharged: 'No te hemos cobrado {amount}.',
    captionRefundHelp: 'Un miembro del personal te ayudará con el reembolso.',
  },
  Success: {
    title: '¡Gracias!',
    caption:
      'Tus retratos han sido impresos. Por favor, recógelos en la recepción.',
    buttonStartAgain: 'Empezar de nuevo',
  },
  GoPhotoScan: {
    title: 'Escanea tu PhotoPass',
    subtitle: 'Muestra el código QR de tu PhotoPass al escáner',
    invalidCode: 'Formato de código inválido. Por favor, escanea de nuevo.',
    failedToRegister:
      'Error al registrar el código. Por favor, inténtalo de nuevo.',
    error: 'Ocurrió un error. Por favor, inténtalo de nuevo.',
  },

  /**
   * Customer-facing components
   */

  CustomerPhotoFromPhone: {
    title: 'Sube fotos desde tu galería',
    buttonSelect: 'Seleccionar fotos',
    maxCount: 'Máximo {count} fotos',
    uploading: 'Subiendo…',
    uploadError:
      'La subida falló. Por favor, comprueba tu conexión e inténtalo de nuevo.',
    buttonRetry: 'Intentar de nuevo',
    successTitle: 'Subida exitosa',
    successCaption:
      'Por favor, regresa al quiosco y sigue las instrucciones en pantalla.',
  },
  CustomerPortraitsDownload: {
    title: 'Introduce el código para obtener tu retrato',
    buttonSubmit: 'Enviar',
    error:
      'Este código no parece correcto. Comprueba el código de 8 caracteres impreso en tu foto e inténtalo de nuevo.',
    errors: {
      wrongFormat:
        'Este código no parece correcto. Comprueba el código de 8 caracteres impreso en tu foto e inténtalo de nuevo.',
      notFound:
        'Lo sentimos, no se encontraron retratos. Por favor verifica el código — está impreso en la parte frontal del retrato, en la esquina inferior izquierda.',
      serverError:
        'Lo sentimos, no pudimos recuperar tus retratos en este momento. Por favor, inténtalo de nuevo más tarde.',
    },
  },
  CustomerSessionPass: {
    tabs: {
      photos: 'Fotos',
      pass: 'Pase QR',
    },
    eyebrow: 'Tu pase de sesión',
    title: 'Mantén abierta esta página',
    description:
      'En el punto de compra, escanea el código cuadrado de abajo. Tus retratos están disponibles en la pestaña Fotos.',
    descriptionNothingPrinted:
      'En el punto de venta, escanea el código cuadrado de abajo para elegir e imprimir tus fotos.',
    scanLabel: 'Escanear en el punto de compra',
    codeLabel: 'Código de sesión',
    ready: 'Listo para escanear',
    loading: 'Preparando el código',
    unavailable: 'Código no disponible',
    qrAlt: 'Código QR para el punto de compra',
    defaultVenue: 'Experiencia de retrato TheArtisan.ai',
  },
  CustomerPortraitsDownloadCode: {
    error: '¡Ups! No se pudo cargar este retrato.',
    placeholder: 'Cargando tu retrato…',
    imageAlt: 'Retrato',
    buttonDownload: 'Descargar',
    buttonSendOrShare: 'Enviar o compartir',
    buttonRetry: 'Intentar de nuevo',
    shareTitle: 'Tu retrato de {clientName}',
    shareText: 'Desarrollado por TheArtisan.ai',
    states: {
      processing:
        'Tus retratos todavía se están preparando. Inténtalo de nuevo en un momento.',
      partial:
        'No se pudieron cargar algunos retratos o detalles de la página. Las descargas disponibles siguen funcionando.',
    },
    actions: {
      enterAnotherCode: 'Introducir otro código',
      contactSupport: 'Contactar con soporte',
      retryDownload: 'Reintentar descarga',
      downloadFailed:
        'La descarga falló. Comprueba tu conexión e inténtalo de nuevo.',
      linkCopied: 'Enlace copiado. Puedes pegarlo en un mensaje.',
      shareFailed: 'No se puede compartir en este momento. Inténtalo de nuevo.',
    },
  },
  CustomerDigitalExtras: {
    downloadTitle: 'Tus fotos y extras',
    downloadIntro: 'Guarda tus fotos, descarga extras o abre tu entrada QR.',
    photosTitle: 'Tus fotos',
    photosIntro: 'Guarda tus fotos o abre tu pase QR.',
    navigation: 'Tus descargas',
    tabs: {
      photos: 'Fotos',
      extras: 'Video',
      pass: 'Entrada QR',
    },
    title: 'Tus extras digitales',
    loading: 'Cargando tus extras…',
    loadError: 'No se pudieron cargar los archivos. Comprueba tu conexión.',
    included: 'Incluido',
    includedOrder:
      'Tu vídeo está incluido. Guarda tu entrada QR para abrirlo más tarde.',
    preparingHelp:
      'Guarda este enlace o tu entrada QR. Puedes cerrar esta página y volver más tarde.',
    reference: 'Referencia: {reference}',
    retry: 'Reintentar',
    empty: 'No se han añadido extras digitales a esta visita.',
    orderTitle: 'Pedido de vídeo',
    showCashier: 'Muestra este pedido y tu entrada QR en caja.',
    videoTotal: 'Total del vídeo',
    payAtDesk: 'Pago en caja.',
    preparing: 'Preparando tus archivos…',
    failed:
      'No se pudo crear tu vídeo. Muestra la referencia de abajo al personal del recinto para recibir ayuda.',
    ready: 'Listo para descargar',
    video: 'Foto animada',
    videoDescription:
      'Tu momento favorito en movimiento. Para tus historias y el chat familiar.',
    download: 'Descargar',
    share: 'Compartir archivo',
    preparingShare: 'Cargando archivo…',
    shareFailed: 'No se puede compartir. Descarga el archivo.',
  },
  GoPhotoUpload: {
    waitingForPortraits: 'Finalizando tus retratos',
    waitingForPortraitsSubtitle:
      'Espera mientras se crean los retratos restantes.',
    success: '¡Fotos subidas!',
    successSubtitle: 'Tus fotos ya están disponibles en tu PhotoPass.',
  },
  system: {
    reconnecting: 'Reconectando con el servidor…',
  },
} satisfies LocaleMessageSchema;
